import * as v from "valibot";

// Which microphone, camera and speaker this browser uses, picked in the
// microphone check. A device that has gone away falls back to the system's.
const ChoicesSchema = v.object({
  microphone: v.optional(v.string()),
  camera: v.optional(v.string()),
  speaker: v.optional(v.string()),
});
type Choices = v.InferOutput<typeof ChoicesSchema>;
export type DeviceKind = keyof Choices;
const storageKey = "ranna:devices:v1";

export function chosenDevices(): Choices {
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved) return v.parse(ChoicesSchema, JSON.parse(saved));
  } catch {
    // Unavailable storage or an invalid saved choice leaves the defaults usable.
  }
  return {};
}

export function chooseDevice(kind: DeviceKind, id: string) {
  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify({ ...chosenDevices(), [kind]: id || undefined }),
    );
  } catch {
    /* Without storage the next call uses the system's devices. */
  }
}

export async function captureMedia(
  video: MediaTrackConstraints | false = false,
) {
  if (!navigator.mediaDevices?.getUserMedia)
    throw new Error("افتح الموقع من متصفح حديث يدعم الكاميرا والميكروفون.");
  const { microphone } = chosenDevices();
  return navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      ...(microphone ? { deviceId: { ideal: microphone } } : {}),
    },
    video,
  });
}

export function mediaError(error: unknown, video: boolean) {
  if (error instanceof Error && error.name === "NotAllowedError")
    return video
      ? "اسمح باستخدام الكاميرا والميكروفون من إعدادات المتصفح."
      : "اسمح باستخدام الميكروفون من إعدادات المتصفح.";
  if (
    error instanceof Error &&
    ["NotFoundError", "OverconstrainedError"].includes(error.name)
  )
    return video
      ? "لا تتوفر كاميرا أو ميكروفون. جرّب مكالمة صوتية."
      : "لا يتوفر ميكروفون.";
  if (error instanceof Error && error.name === "NotReadableError")
    return "تعذر تشغيل الكاميرا أو الميكروفون. أغلق التطبيقات التي تستخدمهما وحاول مجددًا.";
  return error instanceof Error ? error.message : "تعذر بدء المكالمة";
}

export const canChooseSpeaker = () => "setSinkId" in HTMLMediaElement.prototype;

export function applyChosenSpeaker(output: HTMLMediaElement) {
  const { speaker } = chosenDevices();
  if (speaker && canChooseSpeaker())
    void output.setSinkId(speaker).catch(() => {});
}

// A short cue on the speaker just picked, so the choice can be heard.
export function playOnSpeaker(id: string) {
  const context = new AudioContext();
  const tone = context.createOscillator(),
    gain = context.createGain(),
    destination = context.createMediaStreamDestination();
  const output = new Audio();
  output.srcObject = destination.stream;
  const start = context.currentTime;
  tone.frequency.setValueAtTime(523, start);
  tone.frequency.setValueAtTime(659, start + 0.12);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.15, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.36);
  tone.connect(gain).connect(destination);
  tone.onended = () => {
    output.srcObject = null;
    void context.close().catch(() => {});
  };
  void (id ? output.setSinkId(id) : Promise.resolve())
    .then(() => output.play())
    .catch(() => {});
  void context.resume().catch(() => {});
  tone.start(start);
  tone.stop(start + 0.4);
}
